import { allCountries } from 'country-telephone-data';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatWithMask, getPhoneNumberFormat } from './ContactTypeField.utils';

import {
    InputMessage,
    TextField,
    Typography,
} from '@speeki/global-ui-components';

import {
    ButtonsWrapper,
    PhoneInputsRow,
    PhoneInputsWrapper,
    PrefixInput,
    RadioButton,
} from './ContactTypeField.styled';

export type ContactType = 'UNSET' | 'EMAIL' | 'PHONE';

const DEFAULT_PHONE_MASK = Array(15).fill('9').join('');

interface ContactTypeValues {
    contactEmail?: string | null;
    countryCode?: string | null;
    phoneNumber?: string | null;
}

interface WayOfContactProps {
    contactType: ContactType;
    errors: Partial<Record<keyof ContactTypeValues, string>>;
    onChange: (newValue: ContactTypeValues) => void;
    setContactType: (payload: ContactType) => void;
    value: ContactTypeValues;
}

export const ContactTypeField = ({
    contactType,
    errors,
    onChange,
    setContactType,
    value,
}: WayOfContactProps) => {
    const [countryPhoneFormat, setCountryPhoneFormat] =
        useState<string>(DEFAULT_PHONE_MASK);

    const { t } = useTranslation();

    const phoneError = errors.countryCode || errors.phoneNumber;

    const handleChange = (field: keyof ContactTypeValues, fieldValue: string) =>
        onChange({
            ...value,
            [field]: fieldValue,
        });

    const handlePhoneNumberChange = (fieldValue: string) => {
        handleChange(
            'phoneNumber',
            formatWithMask(fieldValue, countryPhoneFormat),
        );
    };

    // NOTE: change phone mask based on country code
    useEffect(() => {
        let newCountryPhoneFormat = DEFAULT_PHONE_MASK;

        if (value?.countryCode) {
            const countryData = allCountries.find(
                ({ dialCode }) =>
                    dialCode === value.countryCode?.replace('+', ''),
            );

            if (countryData?.format)
                newCountryPhoneFormat = getPhoneNumberFormat(
                    countryData.format,
                );
        }

        setCountryPhoneFormat(newCountryPhoneFormat);
    }, [value?.countryCode]);

    // NOTE: update phoneNumber when phone mask changes
    useEffect(() => {
        value?.phoneNumber && handlePhoneNumberChange(value.phoneNumber);
    }, [countryPhoneFormat]);

    return (
        <div>
            {contactType === 'UNSET' && (
                <>
                    <Typography.TextBody_14_20>
                        {t('HOW_DO_YOU_WANT_TO_BE_CONTACTED')}
                    </Typography.TextBody_14_20>
                    <ButtonsWrapper>
                        <RadioButton
                            onClick={() => setContactType('EMAIL')}
                            size={'small'}
                            testId="button_email-text"
                            type={'button'}
                            variant={'radio'}
                        >
                            {t('EMAIL_TEXT')}
                        </RadioButton>
                        <RadioButton
                            onClick={() => setContactType('PHONE')}
                            size={'small'}
                            testId="button_phone-mobile-text"
                            type={'button'}
                            variant={'radio'}
                        >
                            {t('PHONE_MOBILE_TEXT')}
                        </RadioButton>
                    </ButtonsWrapper>
                </>
            )}
            {contactType === 'EMAIL' && (
                <TextField
                    className="htmlToImageIgnore"
                    clickableLabel={{
                        onClick: () => setContactType('PHONE'),
                        text: t('CHANGE_TO_PHONE'),
                    }}
                    error={!!errors.contactEmail}
                    label={t('EMAIL')}
                    message={t(errors.contactEmail || '')}
                    name={'contactEmail'}
                    onChange={(e) =>
                        onChange({
                            ...value,
                            contactEmail: e.target.value,
                        })
                    }
                    placeholder={t('EMAIL_PLACEHOLDER')}
                    testId="PcWNlF_"
                    value={value.contactEmail || ''}
                />
            )}
            {contactType === 'PHONE' && (
                <PhoneInputsWrapper>
                    <PhoneInputsRow>
                        <PrefixInput
                            className="htmlToImageIgnore"
                            error={!!phoneError}
                            label={t('PHONE_NUMBER')}
                            name={'countryCode'}
                            onChange={(e) => {
                                // mask for prefix phone number
                                let input = e.target.value;
                                input = input.replace(/\D/g, ''); // remove all non-digit characters
                                input = input.slice(0, 4); // limit to first 4 digits
                                if (!!input.length) input = `+${input}`; // prepend '+'

                                onChange({
                                    ...value,
                                    countryCode: input,
                                });
                            }}
                            placeholder={'+'}
                            testId="y4L1lME"
                            value={value.countryCode || ''}
                        />
                        <TextField
                            className="htmlToImageIgnore"
                            clickableLabel={{
                                onClick: () => setContactType('EMAIL'),
                                text: t('CHANGE_TO_EMAIL'),
                            }}
                            error={!!phoneError}
                            label={<span />}
                            name={'phoneNumber'}
                            onChange={(e) =>
                                handlePhoneNumberChange(e.target.value)
                            }
                            placeholder={t('PHONE_NUMBER_PLACEHOLDER')}
                            testId="PfIVBfN"
                            value={value.phoneNumber || ''}
                        />
                    </PhoneInputsRow>

                    {phoneError && (
                        <InputMessage
                            iconProps={{ icon: 'SvgAlert24', size: 16 }}
                            testId="Yzbd_a0"
                            variant="error"
                        >
                            {t(errors.countryCode || errors.phoneNumber || '')}
                        </InputMessage>
                    )}
                </PhoneInputsWrapper>
            )}
        </div>
    );
};
